import Link from 'next/link';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRecoilValue } from 'recoil';
import { findEmailState } from '@recoil/auth';
import { useTranslation } from 'next-i18next';

// components
import DefaultLayout from '@components/_templates/DefaultLayout';
import Button from '@components/_atoms/Button';
import Typography from '@components/_atoms/Typography';
import MailIcon from '@public/assets/3dIcons/3dMail.svg';
import { Toast } from '@components/_atoms/Toast';

// hooks
import { useSendPasswordFindEmailFetch } from '@hooks/fetch/useAuthFetch';

// constants
import { MOBILE_MODE } from '@constants/size';
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { gray, primary } from '@styles/Colors';
import { CHANNEL_TALK, FIND } from '@constants/routes/routes';
import ButtonCode from '@constants/log/buttonCode';

const SendWrapper = styled.div`
  width: 100%;
  max-width: 528px;
  padding: 344px 0;
  margin: 0 auto;
  text-align: center;

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 208px 0;
  }
`;

const TitleWrapper = styled.div`
  margin-top: 32px;
`;

const ContentWrapper = styled.div`
  margin-top: 8px;
`;

const ButtonWrapper = styled.div`
  margin-top: 324px;

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;

  @media ${MOBILE_MODE} {
    align-items: stretch;
    margin-top: 108px;
  }
`;

const Send: NextPage = () => {
  const { t, i18n } = useTranslation('auth');
  const ko = i18n.language === 'ko';

  const isMobile = useIsMobile();
  const router = useRouter();

  const findEmail = useRecoilValue(findEmailState);

  const { sendPasswordFindEmailFetchMutate } = useSendPasswordFindEmailFetch();

  // 이메일 X => 찾기 페이지로
  useEffect(() => {
    if (!findEmail) {
      router.replace(FIND);
    }
  }, [findEmail, router]);

  // 이메일 재발송 submit
  const handleSendPasswordVerifySubmit = () => {
    sendPasswordFindEmailFetchMutate(
      { email: findEmail },
      {
        onSuccess: () => {
          Toast.success('이메일을 재발송했어요.');
        },
      },
    );
  };

  return (
    <>
      <Head>
        <title>{t('find.send.meta-title')}</title>
        <meta name="title" content={t('find.send.meta-title')} />
      </Head>
      <DefaultLayout>
        <SendWrapper>
          <MailIcon />
          <TitleWrapper>
            <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.H6} color={primary.gray}>
              {t('find.send.title')}
            </Typography>
          </TitleWrapper>
          <ContentWrapper>
            <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B2} color={gray.gray7}>
              {ko ? (
                <>
                  입력하신 이메일
                  <br />
                  {findEmail}로
                  <br />
                  비밀번호를 재설정 할 수 있는 링크를 발송했어요.
                </>
              ) : (
                <>
                  We have sent a link to reset your password to the email address you provided:
                  <br />
                  {findEmail}
                </>
              )}
            </Typography>
          </ContentWrapper>
          <ButtonWrapper>
            <Button
              text={t('find.send.reSubmit-button')}
              color={ButtonColor.OutlinedGray}
              onClick={handleSendPasswordVerifySubmit}
              steelbosoId={ButtonCode.BTN_SUBMIT}
            />
            <a href={CHANNEL_TALK} target="_blank" rel="noreferrer">
              <Button
                width="100%"
                text={t('find.send.channelTalk-button')}
                color={ButtonColor.TransparentGray}
                steelbosoId={ButtonCode.BTN_CHANNEL_TALK}
              />
            </a>
          </ButtonWrapper>
        </SendWrapper>
      </DefaultLayout>
    </>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['auth', 'common', 'component'])),
  },
});

export default Send;
